import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from "@mui/lab";
import TimelineOppositeContent, { timelineOppositeContentClasses } from "@mui/lab/TimelineOppositeContent";
import { CustomAvatar } from "../../components/custom-avatar";
import { CloseIcon } from "../../../minimals/theme/overrides/CustomIcons";
import axios from "axios";
import { TaskItem } from "../types";
import { getNotes } from "./getNotes";
import InternalUserAutocomplete from "../../shared/InternalUserAutocomplete";
import { InternalUser } from "../../types/app";

export default function NotesModal({
  selectedTaskItem,
  setSelectedTaskItem,
  updateTasks
}: {
  selectedTaskItem: TaskItem;
  setSelectedTaskItem: (value: any) => void;
  updateTasks: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [text, setText] = useState<string>();
  const [selectedAssignedTo, setSelectedAssignedTo] = useState<InternalUser | null>({
    email: selectedTaskItem?.assignedToEmail,
    fullName: selectedTaskItem?.assignedToName
  } as InternalUser);

  const saveNote = async () => {
    if (!selectedTaskItem || !text) return;
    const payload = {
      taskItemId: selectedTaskItem?.id,
      text: text,
      isUserNote: true
    };
    await axios.post("/api/recordablestracking/taskItems/addnote", payload);

    setText(undefined);
  };

  const saveAssignTo = async (user: InternalUser | null) => {
    if (!user || user.email?.toLowerCase() == selectedTaskItem?.assignedToEmail?.toLowerCase()) return;
    const payload = {
      taskItemIds: [selectedTaskItem?.id],
      name: user.fullName,
      email: user.email.toLowerCase(),
      isUserNote: false
    };
    await axios.post("/api/recordablestracking/taskItems/assignTo", payload);
  };

  const updateSelectedTaskItem = async () => {
    if (!selectedTaskItem) return;
    const notes = await getNotes(selectedTaskItem);
    const updatedTaskItem = {
      ...selectedTaskItem,
      notes,
      assignedToName: selectedAssignedTo?.fullName,
      assignedToEmail: selectedAssignedTo?.email
    } as TaskItem;
    setSelectedTaskItem(updatedTaskItem);
    await updateTasks();
    setText(undefined);
  };

  const save = async () => {
    if ((selectedAssignedTo || text) && selectedTaskItem) {
      setSubmitting(true);
      await saveAssignTo(selectedAssignedTo);
      await saveNote();
      await updateSelectedTaskItem();
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setSelectedTaskItem(null);
  };

  const open = !!selectedTaskItem;

  useEffect(() => {
    const fetchAssignedToUser = async () => {
      if (selectedTaskItem?.assignedToEmail) {
        const { data } = await axios.get(
          `/api/clientphonebook/internalusers/getInternalUserByEmail?email=${selectedTaskItem.assignedToEmail}`
        );
        if (data) {
          setSelectedAssignedTo(data);
        }
      }
    };
    fetchAssignedToUser();
  }, []);

  return (
    <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="subtitle1" marginBottom={3}>
          {selectedTaskItem.description}
        </Typography>
        <InternalUserAutocomplete
          sx={{ width: 220 }}
          value={selectedAssignedTo}
          onChange={(val) => {
            setSelectedAssignedTo(val as InternalUser | null);
          }}
          label="Assigned to"
        />
      </DialogTitle>
      <IconButton
        onClick={closeModal}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ paddingBottom: 3 }}>
        <Box paddingTop={1}>
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            rows={3}
            label="Note"
            sx={{ marginBottom: 2 }}
          />
          <Stack direction="row" justifyContent="right">
            <Button variant="contained" size="small" onClick={save} disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
        {selectedTaskItem?.notes && selectedTaskItem?.notes?.length > 0 && (
          <Stack direction="column" spacing={2}>
            <Typography variant="subtitle1">History</Typography>
            <Timeline
              sx={{
                [`& .${timelineOppositeContentClasses.root}`]: {
                  flex: 0.4
                }
              }}
            >
              {selectedTaskItem?.notes?.map((n) => {
                return (
                  <TimelineItem key={n.id}>
                    <TimelineOppositeContent color="textSecondary" width={300}>
                      <Stack direction="column">
                        <Typography variant="caption">{n.createdByUser}</Typography>
                        <Typography variant="caption">
                          {new Date(n.dateCreated).toLocaleDateString()}{" "}
                          {new Date(n.dateCreated).toLocaleTimeString(undefined, {
                            hour: "numeric",
                            minute: "numeric"
                          })}
                        </Typography>
                      </Stack>
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot variant="outlined">
                        <CustomAvatar
                          alt={n.createdByUser}
                          name={n.createdByUser}
                          sx={{ width: 22, height: 22, fontSize: 12 }}
                        />
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {n.text}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
